// useWaitForClientSignature.js
import { useEffect, useRef } from "react";

export default function useWaitForClientSignature({
  formId,
  status,
  isFinalized,
  getClientFormById,
  onSigned,
  enabled = true,
  shouldStop,
  refreshIntervalMs = 4000,
  timeoutMinutes = 10,
  initialDelayMs = 4000,
}) {
  const inFlightRef = useRef(false);
  const cbRef = useRef(onSigned);
  const stopRef = useRef(shouldStop);
  const apiRef = useRef(getClientFormById);

  const timerRef = useRef(null);
  const cancelledRef = useRef(false);
  const startedAtRef = useRef(0);

  useEffect(() => {
    cbRef.current = onSigned;
  }, [onSigned]);

  useEffect(() => {
    stopRef.current = shouldStop;
  }, [shouldStop]);

  useEffect(() => {
    apiRef.current = getClientFormById;
  }, [getClientFormById]);

  useEffect(() => {
    const normalizedStatus = String(status || "").toUpperCase();

    const defaultStop = (cf) => {
      const st = String(cf?.status || "").toUpperCase();
      return st === "SIGNED" || st === "CONFIRMED";
    };
    const stopFn = stopRef.current || defaultStop;

    const shouldWatch =
      enabled &&
      !!formId &&
      !(isFinalized && !stopRef.current) &&
      (normalizedStatus === "PENDING" ||
        normalizedStatus === "SIGNED" ||
        normalizedStatus === "CONFIRMED");

    cancelledRef.current = false;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!shouldWatch) return;

    startedAtRef.current = Date.now();

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const scheduleNext = (ms = refreshIntervalMs) => {
      if (cancelledRef.current) return;
      clearTimer();
      timerRef.current = setTimeout(run, ms);
    };

    const run = async () => {
      if (cancelledRef.current) return;

      const elapsedMin = (Date.now() - startedAtRef.current) / 60000;
      if (elapsedMin > timeoutMinutes) {
        clearTimer();
        return;
      }

      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        scheduleNext(refreshIntervalMs);
        return;
      }

      if (inFlightRef.current) {
        scheduleNext(250);
        return;
      }

      inFlightRef.current = true;
      try {
        const resp = await apiRef.current?.(formId);
        const cf = resp?.data?.clientForm;

        if (!cf) {
          scheduleNext();
          return;
        }

        if (stopFn(cf)) {
          clearTimer();
          await cbRef.current?.(cf);
          return;
        }

        scheduleNext();
      } catch(err) {
        console.error("useWaitForClientSignature: useEffect: run:", err);
        scheduleNext();
      } finally {
        inFlightRef.current = false;
      }
    };

    scheduleNext(initialDelayMs);

    const handleVisibilityChange = () => {
      if (cancelledRef.current) return;
      if (document.visibilityState === "visible") run();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelledRef.current = true;
      clearTimer();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      inFlightRef.current = false;
    };
  }, [formId, status, isFinalized, enabled, refreshIntervalMs, timeoutMinutes, initialDelayMs]);
}